import { get, map } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, Fragment, useState } from 'react';

import ConsentConfirmationModal from 'components/ConsentConfirmationModal';
import EmailSignupModule from 'components/EmailSignupModule';
import HomepageHero from 'components/HomepageHero';
import { MINI_NAV_POSITIONS } from 'components/MiniNavigation/constants';
import PageSectionCardCarousel from 'components/PageSectionCardCarousel';
import PageSectionDualPanelModule from 'components/PageSectionDualPanelModule';
import PageSectionLifestyleCollections from 'components/PageSectionLifestyleCollections';
import PageSectionShoppableFeaturedCollection from 'components/PageSectionShoppableFeaturedCollection';
import PageSectionStatementTextModule from 'components/PageSectionStatementTextModule';
import PageSectionTriPanelModule from 'components/PageSectionTriPanelModule';
import VideoModal from 'components/VideoModal';
import { OVERLAY_POSITIONS, PAGE_SECTIONS_V3 } from 'constants/homepage';
import HeroThemeableModel from 'models/Hero/HeroThemeable';
import PageSectionCardCarouselModel from 'models/PageSectionCardCarousel';
import PageSectionDualPanelModuleModel from 'models/PageSectionDualPanelModule';
import PageSectionEmailSignupModuleModel from 'models/PageSectionEmailSignupModule';
import PageSectionLifestyleCollectionsModel from 'models/PageSectionLifestyleCollections';
import PageSectionShoppableFeaturedCollectionModel from 'models/PageSectionShoppableFeaturedCollection';
import PageSectionStatementTextModuleModel from 'models/PageSectionStatementTextModule';
import PageSectionTriPanelModuleModel from 'models/PageSectionTriPanelModule';
import 'styles/tailwind.css';
import { CONSENT_LIST_STATUS } from 'utils/Iterable/constants';

function HomepageV3({
  closeModal,
  homepageData,
  isModalOpen,
  isMiniNavigationExperimentLoading,
  isMultiTouchCampaignLoading,
  isConsentConfirmationModalOpen,
  miniNavigationPlacementExperimentAnalyticsData,
  consentDecision,
  openModal,
  signUpForNewsletter,
  trackModuleEvent,
}) {
  const [modalVideoUrl, setModalVideoUrl] = useState('');

  const { hero: heroV3, pageSections: _pageSections } = homepageData;
  const hero = new HeroThemeableModel(heroV3);

  const openVideoModal = useCallback(
    url => {
      setModalVideoUrl(url);
      openModal();
    },
    [openModal]
  );

  const handleEmailSubmit = useCallback(
    async (email, acceptsMarketing) => {
      signUpForNewsletter({
        emailAddress: email,
        origination: 'homepageSignupModule',
        acceptsMarketing,
      });
    },
    [signUpForNewsletter]
  );

  const trackCTAClick = useCallback(
    ({ moduleName, moduleType, url, text, modulePosition }) => {
      trackModuleEvent({
        moduleType,
        moduleName,
        modulePosition,
        linkCTAName: text,
        linkDestination: url,
      });
    },
    [trackModuleEvent]
  );

  const pageSections = map(_pageSections, data => {
    switch (get(data, '_contentType')) {
      case PAGE_SECTIONS_V3.STATEMENT_TEXT_MODULE:
        return new PageSectionStatementTextModuleModel(data);
      case PAGE_SECTIONS_V3.DUAL_PANEL_MODULE:
        return new PageSectionDualPanelModuleModel(data);
      case PAGE_SECTIONS_V3.TRI_PANEL_MODULE:
        return new PageSectionTriPanelModuleModel(data);
      case PAGE_SECTIONS_V3.SHOPPABLE_FEATURED_COLLECTION:
        return new PageSectionShoppableFeaturedCollectionModel(data);
      case PAGE_SECTIONS_V3.LIFESTYLE_COLLECTIONS:
        return new PageSectionLifestyleCollectionsModel(data);
      case PAGE_SECTIONS_V3.CARD_CAROUSEL:
        return new PageSectionCardCarouselModel(data);
      case PAGE_SECTIONS_V3.EMAIL_SIGNUP_MODULE:
        return new PageSectionEmailSignupModuleModel(data);
      default:
        return null;
    }
  });

  const renderPageSection = useCallback(
    ({ data, index, key }) => {
      if (!data) return null;

      const pageSectionProps = {
        key,
        trackCTAClick: ({ url, text }) =>
          trackCTAClick({
            moduleName: key,
            modulePosition: index,
            moduleType: data.type,
            text,
            url,
          }),
        ...get(data, '_data', {}),
      };

      switch (data.type) {
        case PAGE_SECTIONS_V3.STATEMENT_TEXT_MODULE:
          return <PageSectionStatementTextModule {...pageSectionProps} />;
        case PAGE_SECTIONS_V3.DUAL_PANEL_MODULE:
          return <PageSectionDualPanelModule {...pageSectionProps} />;
        case PAGE_SECTIONS_V3.TRI_PANEL_MODULE:
          return <PageSectionTriPanelModule {...pageSectionProps} />;
        case PAGE_SECTIONS_V3.SHOPPABLE_FEATURED_COLLECTION:
          return <PageSectionShoppableFeaturedCollection {...pageSectionProps} />;
        case PAGE_SECTIONS_V3.LIFESTYLE_COLLECTIONS:
          return <PageSectionLifestyleCollections {...pageSectionProps} />;
        case PAGE_SECTIONS_V3.CARD_CAROUSEL:
          return <PageSectionCardCarousel {...pageSectionProps} />;
        case PAGE_SECTIONS_V3.EMAIL_SIGNUP_MODULE:
          return <EmailSignupModule {...pageSectionProps} onSubmit={handleEmailSubmit} />;
        /* istanbul ignore next */
        default:
          return null;
      }
    },
    [handleEmailSubmit, trackCTAClick]
  );

  return (
    <div className="Homepage">
      <VideoModal isOpen={isModalOpen} videoUrl={modalVideoUrl} closeModal={closeModal} />
      <ConsentConfirmationModal
        isOpen={isConsentConfirmationModalOpen}
        decision={consentDecision}
        closeModal={closeModal}
      />
      <HomepageHero
        closeModal={closeModal}
        hero={hero}
        isModalOpen={isModalOpen}
        miniNavigationPosition={hero.miniNavigationPosition}
        openVideoModal={openVideoModal}
        isMiniNavigationExperimentLoading={isMiniNavigationExperimentLoading}
        miniNavigationPlacementExperimentAnalyticsData={
          miniNavigationPlacementExperimentAnalyticsData
        }
        overlayPositionDesktop={hero.overlayPositionDesktop}
        overlayPositionMobile={hero.overlayPositionMobile}
      />
      {!isMultiTouchCampaignLoading &&
        map(pageSections, (pageSectionData, index) => (
          <Fragment key={`page-section-${index}`}>
            {renderPageSection({
              data: pageSectionData,
              index,
              key: pageSectionData?.internalName || `page-section-${index}`,
            })}
          </Fragment>
        ))}
    </div>
  );
}

HomepageV3.propTypes = {
  closeModal: PropTypes.func,
  homepageData: PropTypes.shape({
    hero: PropTypes.shape({
      _contentType: PropTypes.string,
      headline: PropTypes.string,
      subheadline: PropTypes.string,
      ctaLinks: PropTypes.arrayOf(PropTypes.shape({})),
      backgroundImage: PropTypes.shape({}),
      miniNavigationPosition: PropTypes.oneOf(Object.values(MINI_NAV_POSITIONS)),
      overlayPositionDesktop: PropTypes.oneOf(Object.values(OVERLAY_POSITIONS)),
      overlayPositionMobile: PropTypes.oneOf(Object.values(OVERLAY_POSITIONS)),
      video: PropTypes.shape({
        file: PropTypes.shape({
          url: PropTypes.string,
        }),
      }),
    }).isRequired,
    pageSections: PropTypes.array,
  }),
  isModalOpen: PropTypes.bool,
  isMiniNavigationExperimentLoading: PropTypes.bool,
  miniNavigationPlacementExperimentAnalyticsData: PropTypes.shape({
    campaignId: PropTypes.string,
    variationId: PropTypes.number,
  }),
  isMultiTouchCampaignLoading: PropTypes.bool,
  openModal: PropTypes.func,
  signUpForNewsletter: PropTypes.func,
  trackModuleEvent: PropTypes.func,
  isConsentConfirmationModalOpen: PropTypes.bool,
  consentDecision: PropTypes.string,
};

HomepageV3.defaultProps = {
  homepageData: {
    hero: {},
    pageSections: [],
  },
  isModalOpen: false,
  isConsentConfirmationModalOpen: false,
  consentDecision: CONSENT_LIST_STATUS.DECLINED,
};

export default HomepageV3;
